import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SharedApiService, ApiType } from './api.service';

export enum DeletionType {
  USER_ACCOUNT = 'user_account'
}

export enum DeletionStatus {
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  VERIFIED = 'VERIFIED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

export interface DeletionRequest {
  id?: number;
  userId?: number;
  status?: string;
  requestedAt?: string;
  identityVerifiedAt?: string;
  processedAt?: string;
  processedBy?: string;
  verificationEmailSentAt?: string;
  rejectionReason?: string;
  retainedDataSummary?: any;
  deletedDataSummary?: any;
  retentionPeriods?: any;
}

export interface CreateDeletionRequestPayload {
  reason: string;
  confirmDeletion: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DeletionRequestService {

  constructor(private readonly sharedApiService: SharedApiService) {}

  /**
   * Create a new deletion request
   */
  createDeletionRequest(payload: CreateDeletionRequestPayload, jobsRequest = false): Observable<DeletionRequest> {
    return this.sharedApiService.post<DeletionRequest>('api/deletion-request', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest }).pipe(
      catchError((error) => {
        console.error('Error creating deletion request:', error);

        // Parse specific backend error messages
        if (error.error?.message?.includes('EXISTING_PENDING_REQUEST')) {
          error.message = 'You already have an active deletion request. Please wait for it to be processed.';
        } else if (error.error?.error?.includes('EXISTING_PENDING_REQUEST')) {
          error.message = 'You already have an active deletion request. Please wait for it to be processed.';
        }

        throw error;
      })
    );
  }

  /**
   * Get deletion request status
   */
  getDeletionRequestStatus(requestId: string, jobsRequest = false): Observable<DeletionRequest> {
    return this.sharedApiService.get<DeletionRequest>(`api/deletion-request/${requestId}`, undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest }).pipe(
      catchError((error) => {
        console.error('Error fetching deletion request status:', error);
        throw error;
      })
    );
  }

  /**
   * Get user's deletion requests
   */
  getUserDeletionRequests(jobsRequest = false): Observable<DeletionRequest[]> {
    return this.sharedApiService.get<DeletionRequest[]>('api/deletion-request', undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest }).pipe(
      map((requests) => {
        return requests || [];
      }),
      catchError((error) => {
        console.error('Error fetching user deletion requests:', error);
        return of([]);
      })
    );
  }

  /**
   * Cancel a pending deletion request
   */
  cancelDeletionRequest(requestId: string, jobsRequest = false): Observable<void> {
    return this.sharedApiService.put<void>(`api/deletion-request/${requestId}/cancel`, {}, undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest }).pipe(
      catchError((error) => {
        console.error('Error cancelling deletion request:', error);
        throw error;
      })
    );
  }

  /**
   * Get deletion policy information
   */
  getDeletionPolicyInfo(): {
    immediatelyDeleted: string[];
    retainedPseudonymized: Array<{ data: string; period: string }>;
  } {
    return {
      immediatelyDeleted: [
        'Name, email, phone, address',
        'CV and attachments',
        'Notes and ratings',
        'Profile data'
      ],
      retainedPseudonymized: [
        { data: 'Application dates and positions', period: '12 months' },
        { data: 'Audit logs', period: '12 months' },
        { data: 'Consent records', period: '24 months' },
        { data: 'IP addresses and technical logs', period: '6 months' }
      ]
    };
  }
}
